import {Component, OnInit} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';
import differenceBy from 'lodash-es/differenceBy';
import {forkJoin} from 'rxjs';
import {ApiService, HttpError} from '../../shared/api.service';
import {AuthService} from '../../shared/auth.service';
import {NavigationService} from '../../shared/navigation.service';

@Component({
  selector: 'timecard-settings-form',
  templateUrl: './timecard-settings-form.component.html',
  styleUrls: ['../styles/account-form.scss', './timecard-settings-form.component.scss']
})
export class TimecardSettingsFormComponent implements OnInit {

  isLoading = false;
  isAddAlternatesShowing = false;
  isEmailAlternates = false;
  possibleAlternates: PossibleAlternate[] = [];
  alternates: Person[] = [];
  masquerading$ = this.Auth.masquerading$;

  constructor(private Api: ApiService,
              private Navigation: NavigationService,
              private Auth: AuthService,
              public Translate: TranslateService) {
  }

  get hasAlternates() {
    return this.alternates.length > 0;
  }

  get hasPossibleAlternates() {
    return this.possibleAlternates.length > 0;
  }

  get hasAvailableAlternates() {
    return this.hasAlternates || this.hasPossibleAlternates;
  }

  ngOnInit() {
    this.loadData();
  }

  loadData() {
    this.isLoading = true;

    forkJoin(
      this.Api.getTimecardSettings(),
      this.Api.getPossibleAlternateSigners(),
      this.Api.getAlternateSigners()
    ).subscribe(([settingsResponse, possibleAlternatesResponse, alternatesResponse]) => {
      this.initializeForm(settingsResponse, possibleAlternatesResponse, alternatesResponse);
      this.isLoading = false;
    }, (err: HttpError) => {
      this.handleError(err);
    });
  }

  initializeForm(settingsResponse: TimecardSettings,
                 possibleAlternatesResponse: AlternateSigners,
                 alternatesResponse: AlternateSigners) {
    this.isEmailAlternates = settingsResponse.isEmailAlternates;
    this.alternates = alternatesResponse.alternateSigners;
    this.initializePossibleAlternates(possibleAlternatesResponse.alternateSigners, alternatesResponse.alternateSigners);
  }

  initializePossibleAlternates(possibleAlternates: Person[], alternates: Person[]) {
    this.possibleAlternates = differenceBy(possibleAlternates, alternates, 'id')
      .map(person => {
        return new PossibleAlternate(person);
      });
  }

  updateEmailAlternates() {
    if (this.masquerading$.getValue()) {
      return;
    }
    this.isLoading = true;

    this.Api.updateTimecardSettings({isEmailAlternates: this.isEmailAlternates}).subscribe(() => {
      this.isLoading = false;
    }, (err: HttpError) => {
      this.handleError(err);
    });
  }

  updateAlternates() {
    if (this.masquerading$.getValue()) {
      return;
    }
    this.isLoading = true;

    this.Api.updateAlternateSigners({ids: this.getAlternateIds()}).subscribe(() => {
      this.isLoading = false;
    }, (err: HttpError) => {
      this.handleError(err);
    });
  }

  getAlternateIds() {
    const alternateIds = [];

    for (const person of this.alternates) {
      alternateIds.push(person.id);
    }

    return alternateIds;
  }

  cancel() {
    this.unselectPossibleAlternates();
    this.isAddAlternatesShowing = false;
  }

  save() {
    this.saveSelectedAlternates();
    this.isAddAlternatesShowing = false;
  }

  unselectPossibleAlternates() {
    this.possibleAlternates.forEach(possible => possible.selected = false);
  }

  removeAlternate(alternateToRemove: Person) {
    if (this.masquerading$.getValue()) {
      return;
    }
    this.alternates = this.alternates.filter(alternate => alternate.id !== alternateToRemove.id);
    this.possibleAlternates.push(new PossibleAlternate(alternateToRemove));
    this.updateAlternates();
  }

  saveSelectedAlternates() {
    this.activateSelectedAlternates();
    this.removeSelectedAlternatesFromPossibles();
    this.updateAlternates();
  }

  activateSelectedAlternates() {
    this.alternates = [...this.alternates, ...this.getSelectedPeople()];
  }

  getSelectedPeople() {
    return this.possibleAlternates.filter(possibleSigner => possibleSigner.selected)
      .map(possibleSigner => {
        return possibleSigner.person;
      });
  }

  removeSelectedAlternatesFromPossibles() {
    this.possibleAlternates = this.possibleAlternates.filter(possibleSigner => !possibleSigner.selected);
  }

  openAddAlternates() {
    this.isAddAlternatesShowing = true;
  }

  isSaveButtonDisabled() {
    return this.possibleAlternates.reduce((previous, current) => previous && !current.selected, true);
  }

  handleError(err: HttpError) {
    this.isLoading = false;
    this.Navigation.handleError(err);
  }
}

class PossibleAlternate {
  person: Person;
  selected: boolean;

  constructor(person: Person) {
    this.person = person;
    this.selected = false;
  }
}
